import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { Button, Menu, MenuItem } from "@material-ui/core";
import TableRowProfile from "../../util/TableRowProfile";
import ConfirmationModal from "../../modal/ConfirmationModal";
import image from "../../../helper/image";
import { ProfileRelationType } from "../../../types/enum";
import "./StaffActRoster.css";

@inject("venueActStore", "templateStore", "userInvitationStore", "profileStore")
@withRouter
@observer
class StaffInvitedActsTable extends Component {
    state = {
        actionAnchor: [],
        removeConfirmationModalState: false,
        selectedInvitation: null,
        resendConfirmationModalState: false,
        resendInvitation: null
    };

    componentDidMount() {
        this.isMountedStaffTable = true;
    }

    componentWillUnmount() {
        this.isMountedStaffTable = false;
    }

    setActionAnchor = (element, id) => {
        const tempAnchor = [];
        tempAnchor[id] = element;
        this.setState({ actionAnchor: tempAnchor });
    };

    unsetActionAnchor = () => {
        this.setState({ actionAnchor: [] });
    };

    openRemoveModal = (invitation) => {
        this.setState({
            removeConfirmationModalState: true,
            selectedInvitation: invitation
        });
    };

    closeRemoveModal = () => {
        this.setState({
            removeConfirmationModalState: false,
            selectedInvitation: null
        });
    };

    handleRemove = () => {
        const { selectedInvitation } = this.state;
        if (selectedInvitation) {
            const context = this.props.venueActStore.captureStaffMutation();
            return this.props.userInvitationStore.declineUserInvitation(selectedInvitation.id).then(() => {
                if (!this.props.venueActStore.isStaffMutationCurrent(context)) return;
                this.props.userInvitationStore.resetInvitations();
                return Promise.all([
                    this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.VenueAct.ordinal),
                    this.props.venueActStore.getStaffPendingActRoster(context.actor),
                    this.props.venueActStore.getStaffActRoster(context.actor)
                ]).then(() => {
                    if (!this.isMountedStaffTable || !this.props.venueActStore.isStaffMutationCurrent(context)) return;
                    this.props.templateStore.openSnackbar("Invitation has been removed.");
                    this.closeRemoveModal();
                });
            });
        }
    };

    openResendModal = (invitation) => {
        this.setState({
            resendConfirmationModalState: true,
            resendInvitation: invitation
        });
    };

    closeResendModal = () => {
        this.setState({
            resendConfirmationModalState: false,
            resendInvitation: null
        });
    };

    handleResend = () => {
        const { resendInvitation } = this.state;
        if (resendInvitation) {
            this.props.userInvitationStore.resendInvitation(resendInvitation).then(() => {
                this.props.templateStore.openSnackbar("Invitation has been resent to " + resendInvitation.invitedAct);
                this.closeResendModal();
            }).catch(() => {
                this.props.templateStore.openSnackbar("Failed to resend invitation. Please try again.");
                this.closeResendModal();
            });
        }
    };

    getFilteredInvitations = () => {
        const { invitations } = this.props.userInvitationStore;
        const { selectedVenueId } = this.props;

        const invited = invitations.filter(inv => !inv.additionalData);

        if (selectedVenueId === "all") {
            return invited;
        }

        const selectedId = String(selectedVenueId);
        return invited.filter(inv => String(inv.venue?.id || '') === selectedId);
    };

    render() {
        const { allowBooking } = this.props;
        const { actionAnchor } = this.state;
        const invitations = this.getFilteredInvitations();

        if (!invitations || invitations.length === 0) {
            return (
                <div className="staff-roster-empty">
                    <i className="fa fa-paper-plane"></i>
                    <p>No pending invitations.</p>
                </div>
            );
        }

        return (
            <Fragment>
                <div className="staff-act-roster-table-container">
                    <table className="staff-pending-table">
                        <thead>
                            <tr>
                                <th style={{ width: "25%" }}>Act</th>
                                <th style={{ width: "20%" }}>Venue</th>
                                <th style={{ width: "25%" }}>Invited Email</th>
                                <th style={{ width: "12%" }}>Status</th>
                                <th style={{ width: "18%" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invitations.map((data, index) => {
                                const rowId = data.id || index;

                                return (
                                    <tr key={rowId}>
                                        <td>
                                            <div className="staff-act-cell">
                                                <TableRowProfile
                                                    profileId={data.act?.id}
                                                    name={data.invitedAct}
                                                    avatarUrl={data.actImage || image.getProfileImage(data.act)}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <div className="staff-venue-cell">
                                                <img
                                                    src={image.getProfileImage(data.venue)}
                                                    className="venue-avatar"
                                                    alt=""
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <span className="venue-name">
                                                    {data.venue?.fullName || "-"}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            {data.invitedEmail || "-"}
                                        </td>
                                        <td>
                                            <span className="staff-status-chip invited">Invited</span>
                                        </td>
                                        <td>
                                            {allowBooking && (
                                                <div className="staff-action-btns">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => this.openResendModal(data)}
                                                    >
                                                        Resend
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        style={{ color: '#f44336', borderColor: '#f44336' }}
                                                        onClick={() => this.openRemoveModal(data)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <ConfirmationModal
                    open={this.state.removeConfirmationModalState}
                    title="Remove Invitation"
                    message={`Are you sure you want to remove the invitation to ${this.state.selectedInvitation?.invitedAct || 'this act'}?`}
                    closeHandler={this.closeRemoveModal}
                    confirmationHandler={this.handleRemove}
                    declineHandler={this.closeRemoveModal}
                    confirmationLabel="Remove"
                />

                <ConfirmationModal
                    open={this.state.resendConfirmationModalState}
                    title="Resend Invitation"
                    message={`Are you sure you want to resend the invitation to ${this.state.resendInvitation?.invitedAct || 'this act'}?`}
                    closeHandler={this.closeResendModal}
                    confirmationHandler={this.handleResend}
                    declineHandler={this.closeResendModal}
                    confirmationLabel="Resend"
                />
            </Fragment>
        );
    }
}

export default StaffInvitedActsTable;
